<template>
  <div
    class="coupon-detail"
    :class="{
      'coupon-detail--disabled': disabled
    }"
    ref="couponDetailRef"
    v-if="coupon"
  >
    <p
      v-if="!show"
      class="coupon-detail__endIn"
      :class="{
        'coupon-detail__endIn--disabled': disabled
      }"
    >
      End in: {{ coupon.endTimeUserStr }}
    </p>
    <div
      class="coupon-detail-preview"
      ref="couponDetailPreviewRef"
      v-if="!show"
      @click="onPreviewClick"
    >
      <img
        class="coupon-detail-preview__gift"
        src="https://cdn-image.bestdealer.com/app-bestdealer/1688/coupon/gift.png"
      >
      <div class="coupon-detail-preview__content">
        <div class="coupon-detail-preview__title">
          <IntlCurrency
            class="coupon-detail-preview__currency"
            currency="us"
            :size="14"
            color="#052785"
          />
          <span class="coupon-detail-preview__amount">{{ coupon.discountAmount }}</span>
          OFF
        </div>
        <span class="coupon-detail-preview__divider" />
        <span class="coupon-detail-preview__text">Total items over US ${{ coupon.minOrderAmount }}</span>
      </div>
      <SvgIcon
        class="coupon-detail-preview__arrow"
        :svg="rightArrowSvg"
        color="#232323"
        width="10px"
        height="10px"
      />
    </div>
    <div
      class="coupon-detail__content"
      ref="couponDetailContentRef"
      v-else
    >
      <p class="coupon-detail__title">
        Spend & Save Coupong
      </p>
      <img
        class="coupon-detail__close"
        src="@/assets/colorIcon/close.svg"
        @click="show = false"
      >
      <div class="coupon-detail-coupon">
        <div class="coupon-detail-coupon__left">
          <div class="coupon-detail__off">
            <IntlCurrency
              class="coupon-detail__currency"
              currency="us"
              :size="14"
              color="#052785"
            />
            <span class="coupon-detail__amount">{{ coupon.discountAmount }}</span>
            OFF
          </div>
          <p class="coupon-detail__total">
            Total items over US ${{ coupon.minOrderAmount }}
          </p>
        </div>
        <div class="coupon-detail-coupon__right">
          <p class="coupon-detail__available">
            Available
          </p>
          <p class="coupon-detail__valid">
            End in: {{ coupon.endTimeUserStr }}
          </p>
        </div>
      </div>
      <div
        class="coupon-detail-delete__wrapper"
        @click="invalidateCoupon(coupon.couponCode)"
      >
        <p class="coupon-detail-delete">
          <img
            class="coupon-detail-delete__icon"
            src="@/assets/colorIcon/delete.svg"
          >
          <span class="coupon-detail-delete__text">Delete</span>
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import rightArrowSvg from '@/assets/offer/right-arrow.svg?raw'
import { useCoupon } from '@/views/Carts/hooks/useCoupon'

const { coupon, invalidateCoupon } = useCoupon()

const show = ref(false)
const couponDetailRef = ref(null)
const couponDetailPreviewRef = ref(null)
const couponDetailContentRef = ref(null)

defineProps({
  disabled: {
    type: Boolean,
    default: false
  }
})

watch(show, () => {
  const el = couponDetailRef.value
  if (!el) return
  nextTick(() => {
    const contentHeight = (show.value ? couponDetailContentRef.value : couponDetailPreviewRef.value)?.offsetHeight
    if (contentHeight) el.style.height = `${contentHeight}px`
  })
})

onMounted(() => {
  const el = couponDetailRef.value
  if (!el) return
  el.style.height = `${el.offsetHeight}px`
})

const onPreviewClick = () => {
  show.value = true
}
</script>

<style lang="less" scoped>
.coupon-detail {
  --opacity: 1;

  position: relative;
  border-radius: 8px 8px 0 0;
  background-color: #DFE8FB;
  font-family: mon-600;
  font-size: 12px;
  color: #052784;
  transition: height .3s ease-in-out;

  &:has(.coupon-detail__content) {
    overflow: hidden;
  }

  &--disabled {
    --opacity: .5;

    background: #EBEBEB;

    .coupon-detail-preview {
      filter: grayscale(1);
      opacity: 0.5;
    }

    .coupon-detail__content {
      filter: grayscale(1);
    }
  }

  @media (min-width: 1024px) {
    border-radius: 4px;
    font-size: 14px;
  }

  &__endIn {
    --bg-color: #1153D6;

    position: absolute;
    top: -13px;
    left: 0;
    display: flex;
    align-items: center;
    height: 18px;
    background: var(--bg-color);
    padding: 2px 8px;
    border-top-right-radius: 10px;
    border-bottom-right-radius: 10px;
    font-family: mon-500;
    font-size: 10px;
    color: #FFFFFF;

    &--disabled {
      --bg-color: #B2B7C2;
    }

    &::after {
      position: absolute;
      bottom: -8px;
      left: 0;
      content: '';
      width: 8px;
      height: 8px;
      background: radial-gradient(circle at 100% 100%, transparent 8px, var(--bg-color) 8px);
    }

    @media (min-width: 1024px) {
      display: none;
    }
  }

  &-preview {
    display: flex;
    align-items: center;
    padding: 10px 8px;
    cursor: pointer;

    @media (min-width: 1024px) {
      padding: 12px 16px;
    }

    &__gift {
      margin-right: 8px;
      width: 24px;
      height: 24px;

      @media (min-width: 1024px) {
        width: 32px;
        height: 32px;
      }
    }

    &__content {
      display: flex;
      align-items: center;

      @media (min-width: 1024px) {
        align-items: flex-start;
        flex-direction: column;
      }
    }

    &__title {
      display: flex;
      align-items: center;
    }

    &__amount {
      font-family: mon-700;
      padding-left: 3px;
      padding-right: 2px;
      font-size: 16px;
      color: #F55745;
    }

    &__divider {
      margin: 0 12px;
      height: 16px;
      border-left: 1px dashed #1B3E9C;

      @media (min-width: 1024px) {
        display: none;
      }
    }

    &__text {
      font-size: 12px;
      background: linear-gradient(90deg, #052784 0%, #733A5F 50%, #E14D39 100%);
      background-clip: text;
      -webkit-background-clip: text;
      color: transparent;
    }

    &__arrow {
      margin-left: auto;

      @media (min-width: 1024px) {
        width: 14px;
        height: 14px;
        align-self: flex-end;
      }
    }
  }

  &__content {
    padding: 12px;
  }

  &__title {
    font-family: mon-600;
    font-size: 14px;
    line-height: 18px;
    opacity: var(--opacity);
  }

  &__close {
    position: absolute;
    top: 15px;
    right: 12px;
    width: 12px;
    opacity: var(--opacity);

    @media (min-width: 1024px) {
      display: none;
    }
  }

  &-coupon {
    /** Coupon style */
    mask-image: radial-gradient(circle at 58% 8px, transparent 8px, red 8.5px), linear-gradient(transparent 25%, red 0px, red 75%, transparent 0px);
    mask-size: 100%, 1px 8px;
    mask-repeat: repeat, repeat-y;
    mask-position: 0px -8px, 58% center;
    mask-composite: subtract;
    background-color: #F5F8FF;
    border-radius: 6px;
    opacity: var(--opacity);

    margin-top: 18px;
    position: relative;
    display: flex;
    height: 88px;

    @media (min-width: 1024px) {
      height: 73px;
    }

    &__left, &__right {
      display: flex;
      justify-content: center;
      align-items: center;
      flex-direction: column;
      text-align: center;
      padding: 0 4px;
    }

    &__left {
      width: 58%;
    }

    &__right {
      width: 42%;
    }
  }

  &__off {
    display: flex;
    align-items: center;

    @media (min-width: 1024px) {
      font-size: 11.2px;
    }
  }

  &__amount {
    padding-left: 4px;
    padding-right: 2px;
    font-family: mon-700;
    font-size: 36px;
    color: #F55745;

    @media (min-width: 1024px) {
      font-size: 24px;
    }
  }

  &__total {
    font-family: mon-600;
    font-size: 12px;
    line-height: 16px;

    @media (min-width: 1024px) {
      font-size: 10px;
    }
  }

  &__available {
    padding: 8px 16px;
    border-radius: 20px;
    border: 1px solid #DFDFDF;
    font-family: mon-500;
    font-size: 12px;
    color: #232323;

    @media (min-width: 1024px) {
      padding: 4px 12px;
      font-size: 10px;
    }
  }

  &__valid {
    margin-top: 6px;
    font-family: mon-500;
    font-size: 10px;
    line-height: 16px;
    color: #6E717A;

    @media (min-width: 1024px) {
      // width: 54px;
    }
  }

  &-delete {
    margin-top: 16px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;

    @media (min-width: 1024px) {
      position: absolute;
      top: 15px;
      right: 12px;
      margin-top: 0;
    }

    &__wrapper {
      text-align: center;
    }

    &__icon {
      width: 16px;
      height: 16px;

      @media (min-width: 1024px) {
        width: 12px;
        height: 12px;
      }
    }

    &__text {
      font-family: mon-500;
      font-size: 12px;
      text-decoration: underline;
      color: #232323;

      @media (min-width: 1024px) {
        font-size: 10px;
      }
    }
  }
}
</style>
